// Considered: PropertyFilterMolecule (inline row body) — rejected because the
// editor BODY needs to be extractable to render full-width in a drill-in overlay
// without row/summary chrome (context.editor.* coupling and collapsible-section
// chrome baked in). This is an extraction (lifting PropertyFilterMolecule's local
// state + value-rendering seams), not a parallel implementation. See spec
// context-selector-editor.md Phase 1 (SC8) Reuse Audit.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { ArcFilter, FilterExpression, FilterOperator, PerspectiveFilter, RelationshipFilter } from 'types/filters';
import InputAtom from '../../atoms/Input/InputAtom';
import CalendarAtom from '../../atoms/Calendar/CalendarAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import MultiSelectAtom from '../../atoms/MultiSelect/MultiSelectAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import DateTimePickerInputAtom from '../../atoms/DateTimePickerInput/DateTimePickerInputAtom';
import UrgencyPillMolecule from '../UrgencyPill/UrgencyPill';
import ArcValueEditorMolecule from '../ArcValueEditor/ArcValueEditorMolecule';
import RelationshipValueEditorMolecule from '../RelationshipValueEditor/RelationshipValueEditorMolecule';
import PerspectiveValueEditorMolecule from '../PerspectiveValueEditor/PerspectiveValueEditorMolecule';
import SphereMultiSelectValueMolecule from '../SphereMultiSelectValue/SphereMultiSelectValueMolecule';
import { SphereTypeSelectMolecule } from '../SphereTypeSelect';
import AspectCatalogMolecule from '../AspectCatalog/AspectCatalogMolecule';
import { propertyOptionsToCatalogEntries } from '../../../utils/catalogBuilders';
import type { OrgAvailableTypeSchema } from 'types/schema';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import type { FilterOperatorOption, TranslateFn } from '../../../utils/filterOperators';
import {
  applyOperatorChange,
  getFilterOperatorOptions,
  readBetweenBounds,
  RELATIVE_DAYS_OPERATORS,
  toBetweenBound,
  toISODateBound,
  VALUELESS_OPERATORS,
} from '../../../utils/filterOperators';
import {
  DEFAULT_ARC_FILTER,
  DEFAULT_PERSPECTIVE_FILTER,
  DEFAULT_RELATIONSHIP_FILTER,
} from '../../../utils/filterPropertyBuilder';
import { relativeDayCount } from '../../../utils/filterLaneSummaries';
import { isRollingDateToken } from '../../../utils/rollingDateLabels';
import RollingDatePresetsMolecule from '../RollingDatePresets/RollingDatePresetsMolecule';
import {
  getFilterTypeAccentVar,
  getFilterTypeIconClassName,
} from '../../../utils/filterTypeVisuals';
import type { PropertyOption, PropertyType } from '../PropertyFilter/PropertyFilterMolecule';
import type { RelationshipOption } from '../RelationshipFilter/RelationshipFilterMolecule';
import type { PerspectiveOption } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import './FilterEditorPanelMolecule.scss';

/** Localization keys for each data type's short label (the badge on the collapsed property chip). */
const PROPERTY_TYPE_LABEL_KEYS: Record<PropertyType, string> = {
  text: 'filterEditor.panel.typeLabel.text',
  date: 'filterEditor.panel.typeLabel.date',
  datetime: 'filterEditor.panel.typeLabel.datetime',
  numeric: 'filterEditor.panel.typeLabel.numeric',
  enum: 'filterEditor.panel.typeLabel.enum',
  boolean: 'filterEditor.panel.typeLabel.boolean',
  urgency: 'filterEditor.panel.typeLabel.urgency',
  arc: 'filterEditor.panel.typeLabel.arc',
  relationship: 'filterEditor.panel.typeLabel.relationship',
  perspective: 'filterEditor.panel.typeLabel.perspective',
  reference: 'filterEditor.panel.typeLabel.reference',
  multi: 'filterEditor.panel.typeLabel.multi',
  person: 'filterEditor.panel.typeLabel.person',
  spheretype: 'filterEditor.panel.typeLabel.spheretype',
  spherefamily: 'filterEditor.panel.typeLabel.spherefamily',
};

/** `@me` — the assignee shortcut every `person`-type value editor seeds ahead of the catalog (filter-row.jsx `EF_PEOPLE[0]`). */
const CURRENT_USER_OPTION_ID = '@me';

/**
 * Caller-supplied wording. The default eyebrow names an ASPECT, which is the context
 * builder's vocabulary; a surface scoping a relationship is choosing a sphere PROPERTY and
 * says so in its own words. Unset, the panel reads as it always has.
 */
export interface FilterEditorPanelCopy {
  /** Replaces the `Property` eyebrow above the property control. */
  propertyLabel?: string;
}

export interface FilterEditorPanelMoleculeProps {
  /** The filter row being edited — property, operator, and value/values. */
  filter: FilterExpression;
  /** The injected property catalog. No context coupling — the caller decides scope/eligibility. */
  availableProperties: PropertyOption[];
  /** Called with the next filter whenever property, operator, or value changes. */
  onChange: (filter: FilterExpression) => void;
  /** Called when the row asks to be discarded (e.g. picker cancelled with no property chosen). */
  onRemove?: () => void;
  /** Read-only rendering — no property/operator/value controls, summary only. */
  readOnly?: boolean;
  /**
   * Excludes property types from the catalog. General-purpose scoping hook for
   * callers that need a narrower picker (e.g. `ApplicabilityEditorMolecule`
   * excluding `urgency`) — not used to keep lane conditions out of group-tree
   * leaves; arc/relationship/perspective are choosable leaves like any other
   * property (EAINT-725).
   */
  excludePropertyTypes?: PropertyType[];
  /**
   * Sphere-type names the picker is scoped to — drives the catalog's scope banner
   * ("Scoped to Priority · Strategy"). Empty/omitted renders the no-scope banner.
   */
  scopeTypeLabels?: string[];
  /** Published arc templates — required for the `type==='arc'` value editor's selector + stage picker. */
  arcTemplates?: ArcTemplateOption[];
  /** Available relationships — required for the `type==='relationship'` value editor's relationship picker. */
  availableRelationships?: RelationshipOption[];
  /** Available perspectives — required for the `type==='perspective'` value editor's perspective picker. */
  availablePerspectives?: PerspectiveOption[];
  /** Schemas for the `type` (Sphere Type) / `label` (Sphere Family) chip-selector value editor. */
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  /** Props for `PerspectiveSearch` (assignee lookup) — forwarded to the `type==='perspective'` value editor. */
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  /**
   * Replaces the default `AspectCatalogMolecule` when the property/lane picker
   * is shown (initial pick, or re-pick via the property summary). Omitted —
   * today's render, unchanged. Exists so a host can swap in
   * `AspectCatalogMolecule` (the six-lane catalog) without this component
   * knowing about lanes, `CatalogEntry`, or fetch states — it only needs to
   * turn whatever the host picks back into a property key.
   */
  renderPropertyPicker?: (opts: { current: string | null; onPick: (key: string) => void; onCancel?: () => void }) => React.ReactNode;
  /** See {@link FilterEditorPanelCopy}. */
  copy?: FilterEditorPanelCopy;
  className?: string;
  /**
   * Focuses the first focusable control of the operator/value editor on
   * mount — a row just added from the catalog (`defaultOpen`) should land
   * the cursor on its value, not require a click first. Ignored while the
   * property picker itself is showing (no `filter.property` yet); that
   * picker manages its own focus.
   */
  autoFocusValue?: boolean;
  /**
   * Parity instrumentation only — tags the "Property" eyebrow above the
   * property summary/picker. The prototype's B1 artboard renders a standalone
   * `<FilterEditorPanel>` a second time (`uf-row.property#3`) beside the drill
   * card's row; omitted everywhere except that dedicated parity story.
   */
  propertyEyebrowDatumId?: string;
  /**
   * Parity instrumentation only — the block whose namespace the Condition/Value
   * eyebrows and the value editor's own controls answer to (`uf-row.condition`/
   * `uf-row.op`/`uf-row.value#2`, or `uf-types.condition`/`uf-types.op`/
   * `uf-types.value#2` — B1's row and B3's per-type gallery are the SAME
   * `<FilterEditorPanel>` render, occurring once in each artboard, so the
   * caller picks which block's datum ids this instance answers to), plus the
   * per-type controls this component already renders 1:1 with the prototype's
   * `EFValueEditor`. Omitted — no instrumentation, today's render. Only the
   * scalar types this component's value editor already matches control-for-
   * control (`text`/`reference`, `numeric` equals/between) are tagged so far —
   * `date`/`enum`/`multi`/`person`/`spheretype`/`spherefamily`/`boolean`
   * render a different control than the prototype's preset-chip date field,
   * combo/pill multi-select, and Yes/No segmented boolean value (a different
   * operator model entirely for boolean — see uf-types gallery comment) and
   * are not tagged until that gap is closed as real UI work, not
   * instrumentation.
   */
  parityBlock?: 'uf-row' | 'uf-types' | 'uf-column';
  /**
   * Parity instrumentation only — override the Condition/operator/Value eyebrow
   * ids the same way `propertyEyebrowDatumId` overrides the property one. B1's
   * artboard renders `<FilterEditorPanel>` three times (two inline demo cards
   * plus the standalone panel beside the drill card): `uf-row.condition`/`.op`/
   * `.value#2` (card 1, the block defaults), `.condition#2`/`.op#2`/`.value#4`
   * (card 2), `.condition#3`/`.op#3`/`.value#5` (the standalone panel — no
   * Apply footer, so no numbered `.apply`). Omitted everywhere except those
   * dedicated parity stories, where the block default would otherwise collide.
   */
  conditionDatumId?: string;
  opDatumId?: string;
  valueDatumId?: string;
  /**
   * Parity instrumentation only — override the `text`/`reference` value input's id
   * the same way `valueDatumId` overrides the Value eyebrow. B1 renders this control
   * three times (two inline demo cards plus the standalone panel): `uf-row.enter-text`
   * (card 1, the block default), `.enter-text#2` (card 2), `.enter-text#3` (the
   * standalone panel). Omitted everywhere except those dedicated parity stories.
   */
  textDatumId?: string;
  /** Parity instrumentation only — same pattern as {@link textDatumId}, for the `numeric` equals input (`uf-row.0`/`.0#2`/`.0#3`). */
  numberDatumId?: string;
  /** Parity instrumentation only — same pattern as {@link textDatumId}, for the `numeric` between range's min input (`uf-row.min`/`.min#2`/`.min#3`). */
  numberMinDatumId?: string;
  /** Parity instrumentation only — same pattern as {@link textDatumId}, for the `numeric` between range's max input (`uf-row.max`/`.max#2`/`.max#3`). */
  numberMaxDatumId?: string;
  /**
   * Parity instrumentation only — tags the collapsed property-summary button
   * (the chip showing the chosen property's icon/label/type badge, shown once
   * `picking` is false and a property is set — B1's `EFPropertyPicker`
   * summary, `context/filter-row.jsx:622-635`). No default: unlike the other
   * overrides above this control has no single unnumbered occurrence in the
   * contract, only numbered ones (`uf-row.button#10`/`#26`/`#40`), so it's
   * tagged only where a caller supplies an id.
   */
  propertySummaryButtonDatumId?: string;
  /** Parity instrumentation only — same pattern as {@link propertySummaryButtonDatumId}, for the summary button's leading type glyph (`uf-row.glyph#8`/`#20`/`#32`). */
  propertySummaryGlyphDatumId?: string;
  /**
   * Parity instrumentation only — forwarded to `ArcValueEditorMolecule`'s own
   * `parityBlock`/override props (see that component) when `prop.type === 'arc'`.
   * B1's row-state gallery renders the arc editor a second and third time
   * (`uf-row.button#11`/`#27`/`#41` etc.), and D3's column adapter a fourth
   * (`uf-column.button#11` etc.); every override below is required (no default)
   * once `parityBlock` here is `'uf-row'`/`'uf-column'`, since ArcValueEditorMolecule
   * cannot infer which numbered occurrence it is standing in for.
   */
  arcTriggerButtonDatumId?: string;
  arcTriggerGlyphDatumId?: string;
  arcMenuButtonDatumId?: string;
  arcMenuGlyphDatumId?: string;
  arcTabsDatumId?: string;
  arcHealthLabelDatumId?: string;
  arcHealthChipDatumId?: string;
  arcStageLabelDatumId?: string;
  arcStageChipDatumId?: string;
  arcLifecycleLabelDatumId?: string;
  arcLifecycleChipDatumId?: string;
  arcAtLeastLabelDatumId?: string;
  /** Parity instrumentation only — forwarded to `ArcValueEditorMolecule`'s minCount stepper decrease button (`uf-arc.button#5` by default). */
  arcMinCountDecreaseDatumId?: string;
  /** Parity instrumentation only — forwarded to `ArcValueEditorMolecule`'s minCount stepper increase button (`uf-arc.button#6` by default). */
  arcMinCountIncreaseDatumId?: string;
  /**
   * Parity instrumentation only — tags the `@me` toggle pill in the `multi`/
   * `person` small-option-set value editor (`useCombo=no`, `context/filter-row.jsx:475`).
   * No default: B1's row-state gallery renders this control three times
   * (`uf-row.me#2`/`#4`/`#6`); the unnumbered `uf-row.me` id already belongs to
   * a different control (the `useCombo=yes` combobox's own `@me` quick-select).
   */
  multiChipMeDatumId?: string;
  /** Parity instrumentation only — same pattern as {@link multiChipMeDatumId}, for the non-`@me` toggle pills (`uf-row.chip#5`/`#10`/`#15`, `context/filter-row.jsx:475`). */
  multiChipDatumId?: string;
  /**
   * Parity instrumentation only — tags the searchable-combo's filter input, shown once
   * `multi`/`person`/`reference` option count exceeds 6 (`useCombo=yes`,
   * `context/filter-row.jsx:530`). Defaults to `${parityBlock}.input#5`.
   */
  multiComboInputDatumId?: string;
  /** Parity instrumentation only — same combo's open/close chevron (`uf-row.button#15`, `context/filter-row.jsx:532`). Defaults to `${parityBlock}.button#15`. */
  multiComboTriggerDatumId?: string;
  /** Parity instrumentation only — same combo's `@me` list item (`uf-row.me`, `context/filter-row.jsx:543`). Defaults to `${parityBlock}.me`. */
  multiComboMeDatumId?: string;
}

/**
 * Sphere Type (`type`) and Sphere Family (`label`) are set-membership properties:
 * a sphere is one of a chosen set. They are authored with the applicability chip
 * selector (multi-select), never a single-value dropdown — so only the `in` /
 * `not_in` operators apply, and their default value is an empty selection array.
 */
function isSphereMembershipKey(key: string | null | undefined): boolean {
  return key === 'type' || key === 'label';
}

/**
 * True for a property typed `spheretype`/`spherefamily` OR keyed `type`/`label`.
 * The type check is the forward path (EAINT-908 catalog entries carry the real
 * type); the key check stays so pre-existing callers that still pass `type`/
 * `label` as plain `enum` continue to render the chip selector unchanged.
 */
/**
 * True for any rolling-date token the engines resolve — `@today` as before, and
 * now every other anchor/offset in the shared grammar
 * (`@center-platform/spheres/filters`). A token is never coerced into a date
 * here: it is written verbatim and resolved server-side, so the filter keeps
 * meaning the same thing tomorrow.
 */
const isDateToken = (v: unknown): boolean => isRollingDateToken(v);

/** A `between` bound: numeric properties carry a number, dates an ISO 8601 string (or a rolling-date token). */
type DateBetweenBound = number | string | undefined;

/**
 * A `between` bound for a date/datetime property, forced into the canonical
 * ISO shape (or left as a rolling-date token). A row's *other* bound can still
 * carry a legacy epoch-ms number when only one side of the pair has been
 * edited since Phase 7a — reading both bounds through this before they reach
 * `changeBounds` keeps the pair the same type, so `compareValues`'s
 * same-type requirement for `between` never sees a stale numeric bound next
 * to a freshly-written ISO one (EAINT-931 Phase 7 review fix).
 */
const normalizeDateBetweenBound = (bound: DateBetweenBound): DateBetweenBound =>
  bound === undefined || isDateToken(bound) ? bound : (toISODateBound(bound) ?? bound);

/** A stored date value as a `Date` for the calendar, or null for empty / a token / an unparseable string. */
const dateFromValue = (v: unknown): Date | null => {
  if (typeof v !== 'string' || !v || isDateToken(v)) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * A `between` bound as a `Date` for the calendar, or null for empty, a
 * rolling-date token, or a value that doesn't parse (`normalizeDateBetweenBound`
 * passes both of the latter through unchanged).
 */
const dateFromBound = (bound: DateBetweenBound): Date | null => {
  if (bound === undefined || bound === '' || isDateToken(bound)) return null;
  const d = new Date(bound);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** A `between` bound as an ISO 8601 string, or null wherever `dateFromBound` yields no date. */
const isoFromBound = (bound: DateBetweenBound): string | null => dateFromBound(bound)?.toISOString() ?? null;

function isSphereMembershipProperty(prop: PropertyOption | undefined): boolean {
  return prop?.type === 'spheretype' || prop?.type === 'spherefamily' || isSphereMembershipKey(prop?.key);
}

/**
 * Operator options for a property, narrowed for sphere-membership properties to
 * the multi-select operators only (their value editor is the chip selector, which
 * always yields an array — Equals/Not-equals single-value semantics don't apply).
 */
function resolveOperatorOptions(
  prop: PropertyOption | undefined,
  t: TranslateFn
): FilterOperatorOption[] {
  if (!prop) return [];
  const options = getFilterOperatorOptions(prop.type, t);
  if (isSphereMembershipProperty(prop)) {
    return options.filter((o) => o.value === 'in' || o.value === 'not_in');
  }
  return options;
}

/** Multi-value property types whose filter carries `values` (a selection array), never a scalar `value`. */
function isMultiValueType(type: PropertyType): boolean {
  return type === 'multi' || type === 'person' || type === 'spheretype' || type === 'spherefamily';
}

function defaultValueForType(type: PropertyType): any {
  if (type === 'arc') return DEFAULT_ARC_FILTER;
  if (type === 'relationship') return DEFAULT_RELATIONSHIP_FILTER;
  if (type === 'perspective') return DEFAULT_PERSPECTIVE_FILTER;
  return type === 'boolean' ? undefined : '';
}

const FilterEditorPanelMolecule: React.FC<FilterEditorPanelMoleculeProps> = ({
  filter,
  availableProperties,
  onChange,
  onRemove,
  readOnly = false,
  excludePropertyTypes,
  scopeTypeLabels,
  arcTemplates = [],
  availableRelationships = [],
  availablePerspectives = [],
  familyTypeSchemas = [],
  perspectiveSearchProps,
  renderPropertyPicker,
  copy,
  className = '',
  propertyEyebrowDatumId,
  parityBlock,
  conditionDatumId,
  opDatumId,
  valueDatumId,
  textDatumId,
  numberDatumId,
  numberMinDatumId,
  numberMaxDatumId,
  propertySummaryButtonDatumId,
  propertySummaryGlyphDatumId,
  arcTriggerButtonDatumId,
  arcTriggerGlyphDatumId,
  arcMenuButtonDatumId,
  arcMenuGlyphDatumId,
  arcTabsDatumId,
  arcHealthLabelDatumId,
  arcHealthChipDatumId,
  arcStageLabelDatumId,
  arcStageChipDatumId,
  arcLifecycleLabelDatumId,
  arcLifecycleChipDatumId,
  arcAtLeastLabelDatumId,
  arcMinCountDecreaseDatumId,
  arcMinCountIncreaseDatumId,
  multiChipMeDatumId,
  multiChipDatumId,
  multiComboInputDatumId,
  multiComboTriggerDatumId,
  multiComboMeDatumId,
  autoFocusValue = false,
}) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const [picking, setPicking] = useState(!filter.property);
  const fieldEditorRef = useRef<HTMLDivElement>(null);

  // Local state for the text input so that every keystroke doesn't propagate
  // to the parent and cause a re-render that remounts this component and loses
  // focus after the first character. The debounce timer flushes to the parent
  // after 300 ms of inactivity; onBlur flushes immediately on leaving the field.
  const [localTextValue, setLocalTextValue] = useState<string>(
    () => (typeof filter.value === 'string' ? filter.value : '')
  );
  const textDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local text when the filter value is changed externally (undo, reset).
  // Skips the sync when the value matches what we already have to avoid wiping
  // an in-progress edit.
  useEffect(() => {
    const external = typeof filter.value === 'string' ? filter.value : '';
    setLocalTextValue((prev) => (prev === external ? prev : external));
  }, [filter.value]);

  // Cancel any pending debounce on unmount to avoid calling a stale onChange.
  useEffect(() => {
    return () => {
      if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
    };
  }, []);

  /** Commit the typed value now, cancelling any pending debounced commit. */
  const flushTextValue = () => {
    if (textDebounceRef.current) {
      clearTimeout(textDebounceRef.current);
      textDebounceRef.current = null;
    }
    changeValue(localTextValue);
  };

  useEffect(() => {
    if (!autoFocusValue || !filter.property) return;
    const el = fieldEditorRef.current?.querySelector<HTMLElement>(
      'input, textarea, select, button, [tabindex]:not([tabindex="-1"])'
    );
    el?.focus();
    // Mount-only: a row opened via `defaultOpen` gets focus once, on its
    // first render — later value/operator changes must never steal it back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const catalog = useMemo(
    () =>
      excludePropertyTypes && excludePropertyTypes.length > 0
        ? availableProperties.filter((p) => !excludePropertyTypes.includes(p.type))
        : availableProperties,
    [availableProperties, excludePropertyTypes]
  );

  // The default picker (no `renderPropertyPicker` slot supplied) is the same
  // catalog every other authoring surface uses — one catalog, one UX.
  const catalogEntries = useMemo(() => propertyOptionsToCatalogEntries(catalog, t), [catalog, t]);

  const prop = filter.property ? catalog.find((p) => p.key === filter.property) : undefined;
  const operatorOptions = useMemo(() => resolveOperatorOptions(prop, t), [prop, t]);
  const isValueless = VALUELESS_OPERATORS.includes(filter.operator as FilterOperator);

  const changeProperty = useCallback(
    (key: string) => {
      const next = catalog.find((p) => p.key === key);
      if (!next) return;
      const ops = resolveOperatorOptions(next, t);
      const keepOperator = prop && ops.some((o) => o.value === filter.operator) ? filter.operator : ops[0]?.value;
      const operator = keepOperator ?? ops[0]?.value ?? 'equals';
      // Sphere Type/Family are set-membership (`in`/`not_in`) — seed an empty
      // `values` array, not `value`, so the row matches the multi-select shape.
      // `id` is carried across deliberately: the row keeps its identity when its
      // property changes, so the React key stays put and the open editor is not
      // remounted mid-edit. Everything else is reset — this is a property swap.
      // Two-coordinate emission (SC2): a composed option carries `aspectSystemName`
      // — the picker always knows it, so the bare-aspect form is never emitted.
      // Synthetic (inline) options have no `aspectSystemName` and keep emitting
      // `{ property }` only.
      const aspect = next.aspectSystemName;
      onChange(
        isSphereMembershipProperty(next) || isMultiValueType(next.type)
          ? { id: filter.id, property: key, aspect, operator, values: [], value: undefined }
          : { id: filter.id, property: key, aspect, operator, value: defaultValueForType(next.type) }
      );
      setPicking(false);
      // Identity metadata (not just the bare key) lets usage analysis tell
      // which aspect a filter selection actually targets (EAINT-899 Phase 5c).
      logAnalyticsEvent('filter_property_selected', undefined, {
        property: next.key,
        propertyType: next.type,
        aspectSystemName: next.aspectSystemName,
        aspectIdentity: next.aspectIdentity,
        aspectFamily: next.aspectFamily,
        definedOn: next.definedOn,
      });
    },
    [catalog, filter.id, filter.operator, logAnalyticsEvent, onChange, prop, t]
  );

  // Delegates to `applyOperatorChange` so the operand-migration rule (which
  // field the new operator reads, and which stale ones must be cleared) has one
  // home shared with every other editor.
  const changeOperator = useCallback(
    (operator: FilterOperator) => {
      onChange(applyOperatorChange(filter, operator, defaultValueForType(prop?.type ?? 'text')));
    },
    [filter, onChange, prop]
  );

  // Scalar operators (equals, contains, lane-filter objects) live in `value`;
  // clear any stale `values` so the two never both seed `currentValue`.
  const changeValue = useCallback(
    (value: any) => {
      onChange({ ...filter, value, values: undefined });
    },
    [filter, onChange]
  );

  // `between` lives in the canonical `min`/`max` pair — the only fields the
  // Gremlin/Elasticsearch/in-memory engines read (`types/filters.d.ts`,
  // `/standards/shared/filtering.md`). Numeric properties carry a number;
  // dates carry an ISO 8601 UTC string (Phase 7a, EAINT-931) — never epoch
  // ms. `value`/`values` are cleared so a row read in the legacy `value:
  // [from, to]` shape (see `readBetweenBounds`) is rewritten canonically on
  // its first edit.
  const betweenBounds = readBetweenBounds(filter);
  const changeBounds = useCallback(
    (bounds: { min?: number | string; max?: number | string }) => {
      onChange({ ...filter, min: bounds.min, max: bounds.max, value: undefined, values: undefined });
    },
    [filter, onChange]
  );

  // Multi-select operators (`in` / `not_in`) live in `values` (plural) — the shape
  // the Gremlin engine (`gremlinFilterBuilder`/`buildUnifiedFilterExpressions` read
  // `expression.values`) and the applicability serializer both require. Writing the
  // array to `value` would make the filter a no-op at execution and crash the
  // level-summary readers, which spread `values`.
  const changeValues = useCallback(
    (values: any[]) => {
      onChange({ ...filter, values, value: undefined });
    },
    [filter, onChange]
  );

  // Seed value: `in`/`not_in` rows carry the selection in `values`; scalar rows in `value`.
  const currentValue = Array.isArray(filter.values) && filter.values.length > 0 ? filter.values : filter.value;

  // Normalize legacy Sphere Type/Family rows to the set-membership shape on open.
  // Before these became set-membership, a row could be authored with a single-value
  // operator (equals/not_equals) and a scalar value (e.g. `'time'`). Those operators
  // are no longer offered (resolveOperatorOptions), so without this the operator pill
  // would show nothing active and the chip selector — which reads an array — would
  // load empty. Coerce operator → in/not_in and value → array so the existing
  // selection surfaces as a chip.
  useEffect(() => {
    if (!prop || !isSphereMembershipKey(prop.key)) return;
    if (filter.operator === 'in' || filter.operator === 'not_in') return;
    const nextOperator: FilterOperator = filter.operator === 'not_equals' ? 'not_in' : 'in';
    let nextValues: unknown[] = [];
    if (Array.isArray(currentValue)) nextValues = currentValue;
    else if (currentValue != null && currentValue !== '') nextValues = [currentValue];
    // Land the selection in `values` (the canonical multi-select shape), not `value`.
    // Spread rather than rebuild: this normalises an existing row, so it must keep
    // the row's `id` (and any other fields) — rebuilding would drop the identity the
    // editor lists key on and remount the row on open.
    onChange({ ...filter, operator: nextOperator, values: nextValues, value: undefined });
    // `filter` is a dependency because the row is now spread rather than rebuilt;
    // it subsumes the `filter.property` / `filter.operator` entries this effect
    // previously listed. Re-running is safe: the guard above returns early once
    // the operator has been normalised to `in`/`not_in`, so this settles after a
    // single pass.
  }, [prop, filter, currentValue, onChange]);

  // Parity datum helpers: a block-scoped id (`<block>.<suffix>`) when this
  // panel stands in for a contract block, else nothing — and a `data-kind`
  // that is only stamped alongside a real id.
  const blockDatum = (suffix: string): string | undefined => (parityBlock ? `${parityBlock}.${suffix}` : undefined);
  const kindFor = (datumId: string | undefined, kind: string): string | undefined => (datumId ? kind : undefined);

  type Prop = NonNullable<typeof prop>;

  // `context/filter-row.jsx:428` — a single Yes/No segmented control. The
  // prototype's boolean type carries one implied condition ("is"); this
  // implementation keeps the operator as the wire value (is_true/is_false)
  // rather than introducing a second `value` field the evaluators would
  // also need to read, so the control writes through `changeOperator`.
  const renderBooleanValue = (): React.ReactNode => (
    <SegmentedControlAtom
      mode="single"
      options={[
        { value: 'is_true', label: t('filters.values.yes'), disabled: readOnly },
        { value: 'is_false', label: t('filters.values.no'), disabled: readOnly },
      ]}
      value={filter.operator === 'is_false' ? 'is_false' : 'is_true'}
      onChange={(v) => !readOnly && changeOperator(v as FilterOperator)}
      data-datum={blockDatum('tabs#3')}
      data-kind={kindFor(parityBlock, 'tabs')}
    />
  );

  const renderTextValue = (): React.ReactNode => {
    const datumId = textDatumId ?? blockDatum('enter-text');
    return (
      <InputAtom
        className="filter-editor-panel-molecule__value-input"
        value={localTextValue}
        onChange={(e) => {
          const v = e.target.value;
          setLocalTextValue(v);
          // Debounce parent propagation — local state update keeps focus;
          // parent re-renders only after typing pauses.
          if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
          textDebounceRef.current = setTimeout(() => changeValue(v), 300);
        }}
        onBlur={flushTextValue}
        // Enter commits without leaving the field, so it must flush too — otherwise a
        // value typed and confirmed inside the debounce window is applied as the
        // previous one. Clicking a button flushes via the blur focus move.
        onKeyDown={(e) => {
          if (e.key === 'Enter') flushTextValue();
        }}
        placeholder={t('filters.placeholders.enterText')}
        disabled={readOnly}
        data-datum={datumId}
        data-kind={kindFor(datumId, 'input')}
      />
    );
  };

  const renderNumericValue = (p: Prop): React.ReactNode => {
    if (p.type === 'urgency') {
      return (
        <UrgencyPillMolecule
          urgencyValue={Number.parseInt(currentValue || '3', 10)}
          onUrgencyChange={(urgency) => changeValue(urgency)}
        />
      );
    }
    if (filter.operator === 'between') {
      const { min, max } = betweenBounds;
      const minDatumId = numberMinDatumId ?? blockDatum('min');
      const maxDatumId = numberMaxDatumId ?? blockDatum('max');
      return (
        <div className="filter-editor-panel-molecule__between">
          <InputAtom
            className="filter-editor-panel-molecule__value-input"
            type="number"
            value={min === undefined ? '' : String(min)}
            onChange={(e) => changeBounds({ min: toBetweenBound(e.target.value), max })}
            placeholder={t('filters.placeholders.min')}
            disabled={readOnly}
            data-datum={minDatumId}
            data-kind={kindFor(minDatumId, 'input')}
          />
          <span className="filter-editor-panel-molecule__between-to">{t('filters.placeholders.to')}</span>
          <InputAtom
            className="filter-editor-panel-molecule__value-input"
            type="number"
            value={max === undefined ? '' : String(max)}
            onChange={(e) => changeBounds({ min, max: toBetweenBound(e.target.value) })}
            placeholder={t('filters.placeholders.max')}
            disabled={readOnly}
            data-datum={maxDatumId}
            data-kind={kindFor(maxDatumId, 'input')}
          />
        </div>
      );
    }
    const datumId = numberDatumId ?? blockDatum('0');
    return (
      <InputAtom
        className="filter-editor-panel-molecule__value-input"
        type="number"
        value={currentValue ?? ''}
        onChange={(e) => changeValue(e.target.value)}
        placeholder={t('filters.placeholders.enterNumber')}
        disabled={readOnly}
        data-datum={datumId}
        data-kind={kindFor(datumId, 'input')}
      />
    );
  };

  // `context/filter-row.jsx:588` (`EFDateValue`) leads the date value editor with a
  // row of relative-date quick-picks (`@today`, "start of week", "+7d", …). Every
  // one of them is now a real, evaluator-resolved token: the rolling-date grammar
  // (`@center-platform/spheres/filters`) is resolved server-side by
  // `resolveFilterVariables.ts` / `resolveSearchFilterVariables.ts` before any
  // engine runs, and client-side by `useColumnFilters` through the same module.
  // So the whole row is wired, not just `@today` — and the builder behind it can
  // compose any anchor/offset the grammar accepts.
  const datePresets = (datumId?: string) => (
    <RollingDatePresetsMolecule
      value={currentValue}
      onPick={(token) => changeValue(token)}
      readOnly={readOnly}
      todayDatumId={datumId}
    />
  );

  /** The same presets writing one `between` bound rather than the scalar value. */
  const boundPresets = (
    bound: DateBetweenBound,
    write: (token: string) => void,
    datumId?: string
  ) => (
    <RollingDatePresetsMolecule
      value={bound}
      onPick={(token) => write(token)}
      readOnly={readOnly}
      todayDatumId={datumId}
    />
  );

  // `within_last_days` / `within_next_days` / `date_within_days` carry a plain
  // day count (see RELATIVE_DAYS_OPERATORS) — a number input, not the
  // date-range/calendar controls the other date operators use. The operator's
  // own label names no number ("In the last", "Within (past or future)"), so the
  // unit reads after the count here — days being the only granularity the
  // engines evaluate. Shared by `date` and `datetime`: both store an instant and
  // the window is computed in whole days either way.
  const renderRelativeDaysValue = (): React.ReactNode => {
    const dayCount = relativeDayCount(filter);
    return (
      <div className="filter-editor-panel-molecule__relative-days">
        <InputAtom
          className="filter-editor-panel-molecule__value-input"
          type="number"
          min={1}
          value={currentValue ?? ''}
          onChange={(e) => changeValue(e.target.value)}
          placeholder={t('filters.placeholders.enterDays')}
          disabled={readOnly}
          data-datum={blockDatum('input')}
          data-kind={kindFor(parityBlock, 'input')}
        />
        <span className="filter-editor-panel-molecule__relative-days-unit">
          {t('filterEditor.row.relativeDaysUnit', { count: dayCount ?? 0 })}
        </span>
      </div>
    );
  };

  const renderDateValue = (): React.ReactNode => {
    if (RELATIVE_DAYS_OPERATORS.includes(filter.operator as FilterOperator)) return renderRelativeDaysValue();
    if (filter.operator === 'between') {
      // Each bound gets its OWN preset row, independently: a range is two
      // questions, and `between @startOfQuarter .. @endOfQuarter` is only
      // expressible if each end can name its own anchor. `min`/`max` otherwise
      // stay canonical ISO date strings — a token is written verbatim and
      // resolved downstream (to that day's first instant for `min` and its last
      // for `max`, in the viewer's zone), exactly like the single-date token.
      const min = normalizeDateBetweenBound(betweenBounds.min);
      const max = normalizeDateBetweenBound(betweenBounds.max);
      return (
        <div className="filter-editor-panel-molecule__between">
          <div className="filter-editor-panel-molecule__date-with-presets" data-container="each(EF_DATE_PRESETS)">
            {boundPresets(min, (token) => changeBounds({ min: token, max }), blockDatum('p'))}
            <span
              className="filter-editor-panel-molecule__between-bound"
              data-datum={blockDatum('input#2')}
              data-kind={kindFor(parityBlock, 'input')}
            >
              <CalendarAtom
                value={dateFromBound(min)}
                onChange={(e) => changeBounds({ min: toISODateBound(e.target.value), max })}
                showIcon
                disabled={readOnly}
                ariaLabel={t('filters.placeholders.startDate')}
              />
            </span>
          </div>
          <span className="filter-editor-panel-molecule__between-to">{t('filters.placeholders.to')}</span>
          <div className="filter-editor-panel-molecule__date-with-presets" data-container="each(EF_DATE_PRESETS)">
            {boundPresets(max, (token) => changeBounds({ min, max: token }), blockDatum('p'))}
            <span className="filter-editor-panel-molecule__between-bound">
              <CalendarAtom
                value={dateFromBound(max)}
                onChange={(e) => changeBounds({ min, max: toISODateBound(e.target.value) })}
                showIcon
                disabled={readOnly}
                ariaLabel={t('filters.placeholders.endDate')}
              />
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className="filter-editor-panel-molecule__date-with-presets" data-container="each(EF_DATE_PRESETS)">
        {datePresets(blockDatum('p#3'))}
        <span data-datum={blockDatum('input#4')} data-kind={kindFor(parityBlock, 'input')}>
          <CalendarAtom
            value={dateFromValue(currentValue)}
            onChange={(e) => changeValue(e.target.value?.toISOString?.() ?? '')}
            showIcon
            disabled={readOnly}
            ariaLabel={t('filterEditor.panel.valueLabel')}
          />
        </span>
      </div>
    );
  };

  // A `datetime` property gets the same presets a `date` one does. A rolling
  // date names a calendar DAY, and the server expands a bound to that day's
  // first/last instant in the viewer's zone (`resolveBound`), so the token is
  // exactly as meaningful against a stored instant as against a stored date —
  // and a datetime row that could not say "this quarter" while the date row
  // beside it could would read as a bug, not a distinction.
  const renderDatetimeValue = (): React.ReactNode => {
    if (RELATIVE_DAYS_OPERATORS.includes(filter.operator as FilterOperator)) return renderRelativeDaysValue();
    if (filter.operator === 'between') {
      const min = normalizeDateBetweenBound(betweenBounds.min);
      const max = normalizeDateBetweenBound(betweenBounds.max);
      return (
        <div className="filter-editor-panel-molecule__between">
          <div className="filter-editor-panel-molecule__date-with-presets" data-container="each(EF_DATE_PRESETS)">
            {boundPresets(min, (token) => changeBounds({ min: token, max }))}
            <DateTimePickerInputAtom
              value={isoFromBound(min)}
              onIsoChange={(iso) => changeBounds({ min: toISODateBound(iso), max })}
              disabled={readOnly}
            />
          </div>
          <span className="filter-editor-panel-molecule__between-to">{t('filters.placeholders.to')}</span>
          <div className="filter-editor-panel-molecule__date-with-presets" data-container="each(EF_DATE_PRESETS)">
            {boundPresets(max, (token) => changeBounds({ min, max: token }))}
            <DateTimePickerInputAtom
              value={isoFromBound(max)}
              onIsoChange={(iso) => changeBounds({ min, max: toISODateBound(iso) })}
              disabled={readOnly}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="filter-editor-panel-molecule__date-with-presets" data-container="each(EF_DATE_PRESETS)">
        {datePresets()}
        <DateTimePickerInputAtom
          // A token is not an instant: handing it to the picker would either
          // render as an invalid date or be silently rewritten to one, losing
          // the author's relative intent on the next edit.
          value={isDateToken(currentValue) ? null : currentValue}
          onIsoChange={(iso) => changeValue(iso)}
          disabled={readOnly}
        />
      </div>
    );
  };

  // Sphere Type (`type`) / Sphere Family (`label`) always use the applicability
  // chip selector, constrained to the relevant axis. These are set-membership
  // properties (in / not_in only — see resolveOperatorOptions), so the value is
  // always a selection array and never falls through to the enum dropdown.
  const renderMembershipValue = (p: Prop): React.ReactNode => {
    const isFamily = p.type === 'spherefamily' || p.key === 'label';
    let selected: string[] = [];
    if (Array.isArray(currentValue)) selected = currentValue as string[];
    else if (currentValue != null && currentValue !== '') selected = [String(currentValue)];
    return (
      <SphereTypeSelectMolecule
        schemas={familyTypeSchemas}
        axis={isFamily ? 'family' : 'type'}
        selectedFamilies={isFamily ? selected : []}
        selectedTypes={isFamily ? [] : selected}
        onFamiliesChange={isFamily ? changeValues : () => undefined}
        onTypesChange={isFamily ? () => undefined : changeValues}
        readOnly={readOnly}
      />
    );
  };

  const renderMultiValue = (p: Prop): React.ReactNode => {
    // `person` seeds an `@me` shortcut ahead of the catalog (filter-row.jsx
    // `EF_PEOPLE[0]`) whenever the caller's `enumValues` didn't already supply
    // one — a caller-provided `@me` (with a real display label) always wins.
    const options =
      p.type === 'person' && !p.enumValues?.some((ev) => ev.value === CURRENT_USER_OPTION_ID)
        ? [{ label: t('filterEditor.panel.currentUserOption'), value: CURRENT_USER_OPTION_ID }, ...(p.enumValues ?? [])]
        : (p.enumValues ?? []);
    const selected = Array.isArray(currentValue) ? (currentValue as string[]) : [];
    // Small, closed option sets render as always-visible toggle pills (no
    // search, nothing hidden behind a click) — `EFOptionPill`,
    // `context/filter-row.jsx:184` / `:459` (`useCombo` false once
    // `options.length <= 6`). Larger/open-ended sets keep the searchable
    // combobox (`EFMultiSelect`, same file `:481`) via `MultiSelectAtom`.
    if (options.length <= 6) {
      const toggle = (value: string) =>
        changeValues(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
      return (
        <div className="filter-editor-panel-molecule__value-pills" data-container="each(options)">
          {options.map((option) => {
            const chipDatumId = option.value === CURRENT_USER_OPTION_ID ? multiChipMeDatumId : multiChipDatumId;
            return (
              <button
                key={option.value}
                type="button"
                className={`filter-editor-panel-molecule__operator-pill${selected.includes(option.value) ? ' filter-editor-panel-molecule__operator-pill--active' : ''}`}
                onClick={() => toggle(option.value)}
                disabled={readOnly}
                aria-pressed={selected.includes(option.value)}
                data-testid={`filter-editor-panel-value-pill-${option.value}`}
                data-datum={chipDatumId}
                data-kind={kindFor(chipDatumId, 'chip')}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      );
    }
    const comboInputDatumId = multiComboInputDatumId ?? blockDatum('input#5');
    const comboTriggerDatumId = multiComboTriggerDatumId ?? blockDatum('button#15');
    const comboMeDatumId = multiComboMeDatumId ?? blockDatum('me');
    const comboOptions = options.map((ev) => ({ label: ev.label, value: ev.value }));
    return (
      <MultiSelectAtom
        value={selected}
        options={comboOptions}
        onChange={(e) => changeValues(e.value)}
        placeholder={t('filters.placeholders.selectValues')}
        disabled={readOnly}
        filter
        // uf-row's combobox words its empty-filter state "No matches."
        // (`context/filter-row.jsx:539`, `EFMultiSelect`) — PrimeReact's own
        // default is "No results found".
        emptyFilterMessage={t('filterEditor.panel.noComboMatches')}
        // The prototype's search field and dropdown list sit inline in the row
        // (`context/filter-row.jsx:517`'s comment: "so it never clips inside the
        // scroll area") rather than PrimeReact's own-portal default, which would
        // detach the panel from `EditableFilterRowMolecule`'s DOM subtree.
        appendTo="self"
        pt={{
          // `InputText` reads its own `pt` prop for its `root` element, so the
          // filter input's attrs must nest under `root` rather than sit flat.
          filterInput: { root: { 'data-datum': comboInputDatumId, 'data-kind': kindFor(comboInputDatumId, 'input') } },
          // The prototype's dropdown toggle (`uf-row.button#15`) is, in PrimeReact, the
          // keyboard combobox `<input>` — the element that takes focus and opens the list.
          // The chevron `<div>` beside it is decorative (giving it `role="button"` made an
          // unnamed, unfocusable button, axe `aria-command-name`), and the visible root is
          // a plain `<div>`. The input is screen-reader-only, which GEO-1 does not judge.
          input: comboTriggerDatumId
            ? { 'data-datum': comboTriggerDatumId, 'data-kind': 'button' }
            : {},
          // `item` targets the real `<li role="option">` (PrimeReact's actionable
          // list row), keyed by its position in the visible/filtered option list —
          // reliable for the unfiltered rendering these parity captures exercise.
          item: (itemOptions: { context: { index: number } }) => {
            const isMe = comboOptions[itemOptions.context.index]?.value === CURRENT_USER_OPTION_ID;
            // Every option row is one of the prototype's `each(filtered)` collection.
            return isMe
              ? { 'data-container': 'each(filtered)', 'data-datum': comboMeDatumId, 'data-kind': kindFor(comboMeDatumId, 'button') }
              : { 'data-container': 'each(filtered)' };
          },
        }}
      />
    );
  };

  /**
   * A reference stores the target sphere's identifier. Compared by set
   * membership (`in` / `not_in` — "is any of" / "is none of") the answer is a
   * LIST of spheres, so it is picked through the same searchable, scoped,
   * permission-filtered picker the relationship lane uses, never typed as raw
   * ids. `equals`/`not_equals` keep the free-text identifier input they have had
   * since `reference` was split out of `text` (Phase 8) — a single-sphere picker
   * is a separate change, and swapping the control for one operator pair while
   * leaving the other on free text would be the inconsistency, not the fix.
   */
  const renderReferenceValue = (p: Prop): React.ReactNode => {
    if (filter.operator !== 'in' && filter.operator !== 'not_in') return renderTextValue();
    const selected = Array.isArray(filter.values) ? (filter.values as string[]) : [];
    return (
      <SphereMultiSelectValueMolecule
        value={selected}
        onChange={changeValues}
        targetSchemaSystemName={p.referenceTargetSchema}
        readOnly={readOnly}
        ariaLabel={p.label}
      />
    );
  };

  const renderEnumValue = (p: Prop): React.ReactNode => {
    // `in`/`not_in` is a set-membership answer — the same editor as a multi-valued
    // property: toggle pills for a short set, the inline searchable combo for a long
    // one. An overlay multi-select would detach from its field as soon as the
    // drill-in this panel lives in is scrolled.
    if (filter.operator === 'in' || filter.operator === 'not_in') return renderMultiValue(p);
    const options = (p.enumValues ?? []).map((ev) => ({ label: ev.label, value: ev.value }));
    return (
      <DropdownAtom
        value={typeof currentValue === 'string' ? currentValue : null}
        options={options}
        onChange={(e) => changeValue(e.value)}
        placeholder={t('filters.placeholders.selectValue')}
        disabled={readOnly}
        filter
      />
    );
  };

  // The value section's editor, by property type. Boolean is the one type whose
  // two operators (is_true/is_false) ARE the valueless-per-VALUELESS_OPERATORS
  // pair, so the generic `isValueless` gate would otherwise hide it entirely —
  // every other type's value section is genuinely optional; boolean's isn't.
  const renderValueEditor = (): React.ReactNode => {
    if (!prop) return null;
    if (isValueless && prop.type !== 'boolean') return null;
    if (prop.type === 'boolean') return renderBooleanValue();
    if (prop.type === 'text') return renderTextValue();
    if (prop.type === 'reference') return renderReferenceValue(prop);
    if (prop.type === 'numeric' || prop.type === 'urgency') return renderNumericValue(prop);
    if (prop.type === 'date') return renderDateValue();
    if (prop.type === 'datetime') return renderDatetimeValue();
    if (isSphereMembershipProperty(prop)) return renderMembershipValue(prop);
    if (prop.type === 'enum') return renderEnumValue(prop);
    if (prop.type === 'multi' || prop.type === 'person') return renderMultiValue(prop);
    return null;
  };

  // The Property field: the catalog (or the caller's own picker) while choosing,
  // the closed summary button once a property is set.
  const renderPropertyArea = (): React.ReactNode => {
    if (picking || !prop) {
      const onCancel = prop ? () => setPicking(false) : onRemove;
      if (renderPropertyPicker) {
        return renderPropertyPicker({ current: filter.property || null, onPick: changeProperty, onCancel });
      }
      return (
        <AspectCatalogMolecule
          selectedKey={filter.property || null}
          entries={catalogEntries}
          onPick={(entry) => changeProperty(entry.key)}
          onCancel={onCancel}
          scopeTypeLabels={scopeTypeLabels}
          testIdPrefix="filter-editor-panel-property"
        />
      );
    }
    return (
      <button
        type="button"
        className="filter-editor-panel-molecule__property-summary"
        onClick={() => !readOnly && setPicking(true)}
        disabled={readOnly}
        data-testid="filter-editor-panel-property-summary"
        data-datum={propertySummaryButtonDatumId}
        data-kind={kindFor(propertySummaryButtonDatumId, 'button')}
      >
        <i
          className={getFilterTypeIconClassName(prop.type)}
          aria-hidden="true"
          data-datum={propertySummaryGlyphDatumId}
          data-kind={kindFor(propertySummaryGlyphDatumId, 'glyph')}
        />
        <span className="filter-editor-panel-molecule__property-summary-label">{prop.label}</span>
        <span className="filter-editor-panel-molecule__property-summary-type">
          {t(PROPERTY_TYPE_LABEL_KEYS[prop.type])}
        </span>
        {!readOnly && <i className="pi pi-chevron-down" aria-hidden="true" />}
      </button>
    );
  };

  const renderFieldEditor = (): React.ReactNode => {
    if (!prop || picking) return null;
    if (prop.type === 'arc') return (
      <div className="filter-editor-panel-molecule__field">
        <ArcValueEditorMolecule
          arcTemplates={arcTemplates}
          value={(currentValue as ArcFilter) ?? DEFAULT_ARC_FILTER}
          onChange={changeValue}
          readOnly={readOnly}
          parityBlock={parityBlock === 'uf-row' || parityBlock === 'uf-column' ? parityBlock : undefined}
          triggerButtonDatumId={arcTriggerButtonDatumId}
          triggerGlyphDatumId={arcTriggerGlyphDatumId}
          menuButtonDatumId={arcMenuButtonDatumId}
          menuGlyphDatumId={arcMenuGlyphDatumId}
          tabsDatumId={arcTabsDatumId}
          healthLabelDatumId={arcHealthLabelDatumId}
          healthChipDatumId={arcHealthChipDatumId}
          stageLabelDatumId={arcStageLabelDatumId}
          stageChipDatumId={arcStageChipDatumId}
          lifecycleLabelDatumId={arcLifecycleLabelDatumId}
          lifecycleChipDatumId={arcLifecycleChipDatumId}
          atLeastLabelDatumId={arcAtLeastLabelDatumId}
          minCountDecreaseDatumId={arcMinCountDecreaseDatumId}
          minCountIncreaseDatumId={arcMinCountIncreaseDatumId}
        />
      </div>
    );
    if (prop.type === 'relationship') return (
      <div className="filter-editor-panel-molecule__field">
        <RelationshipValueEditorMolecule
          availableRelationships={availableRelationships}
          value={(currentValue as RelationshipFilter) ?? DEFAULT_RELATIONSHIP_FILTER}
          onChange={changeValue}
          readOnly={readOnly}
        />
      </div>
    );
    if (prop.type === 'perspective') return (
      <div className="filter-editor-panel-molecule__field">
        <PerspectiveValueEditorMolecule
          availablePerspectives={availablePerspectives}
          perspectiveSearchProps={perspectiveSearchProps}
          value={(currentValue as PerspectiveFilter) ?? DEFAULT_PERSPECTIVE_FILTER}
          onChange={changeValue}
          readOnly={readOnly}
        />
      </div>
    );
    // Boolean carries exactly one implied condition (`context/filter-row.jsx`'s
    // EF_OPERATORS.boolean === ["is"]) — the Condition pills below exist to pick
    // among 2+ real operators, which boolean never has, so they're skipped and
    // the Yes/No choice renders as the Value section's own segmented control.
    const isBoolean = prop.type === 'boolean';
    return (
      <>
        {!isBoolean && (
          <div className="filter-editor-panel-molecule__field">
            <div
              className="filter-editor-panel-molecule__eyebrow"
              data-datum={conditionDatumId ?? (parityBlock ? `${parityBlock}.condition` : undefined)}
              data-kind={conditionDatumId || parityBlock ? 'label' : undefined}
            >
              {t('filterEditor.panel.conditionLabel')}
            </div>
            <div className="filter-editor-panel-molecule__operators" data-container="each(ops)">
              {operatorOptions.map((op) => (
                <button
                  key={op.value}
                  type="button"
                  className={`filter-editor-panel-molecule__operator-pill${filter.operator === op.value ? ' filter-editor-panel-molecule__operator-pill--active' : ''}`}
                  onClick={() => changeOperator(op.value)}
                  disabled={readOnly}
                  aria-pressed={filter.operator === op.value}
                  data-testid={`filter-editor-panel-operator-${op.value}`}
                  data-datum={opDatumId ?? (parityBlock ? `${parityBlock}.op` : undefined)}
                  data-kind={opDatumId || parityBlock ? 'button' : undefined}
                >
                  {op.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {(!isValueless || isBoolean) && (
          <div className="filter-editor-panel-molecule__field">
            <div
              className="filter-editor-panel-molecule__eyebrow"
              data-datum={valueDatumId ?? (parityBlock ? `${parityBlock}.value#2` : undefined)}
              data-kind={valueDatumId || parityBlock ? 'label' : undefined}
            >
              {t('filterEditor.panel.valueLabel')}
            </div>
            {renderValueEditor()}
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={`filter-editor-panel-molecule ${className}`.trim()}
      data-testid="filter-editor-panel"
      style={prop ? ({ '--fep-accent': getFilterTypeAccentVar(prop.type) } as React.CSSProperties) : undefined}
    >
      <div className="filter-editor-panel-molecule__field">
        <div
          className="filter-editor-panel-molecule__eyebrow"
          // `propertyEyebrowDatumId` overrides for the numbered occurrence
          // (`uf-row.property#3`, the standalone panel beside the drill
          // card); otherwise the block's own non-numbered `.property` id —
          // this eyebrow is always present once `showEditor` is true,
          // independent of `prop.type`, so it must not stay untagged just
          // because no override was supplied.
          data-datum={propertyEyebrowDatumId ?? (parityBlock ? `${parityBlock}.property` : undefined)}
          data-kind={propertyEyebrowDatumId || parityBlock ? 'label' : undefined}
        >
          {copy?.propertyLabel ?? t('filterEditor.panel.propertyLabel')}
        </div>
        {renderPropertyArea()}
      </div>

      <div ref={fieldEditorRef}>{renderFieldEditor()}</div>
    </div>
  );
};

export default FilterEditorPanelMolecule;
